/**
 * FeedbackOverlay Component
 * -------------------------
 * Studio Console Design: Subtle flash overlays for note feedback
 * Shows brief color flash on correct/wrong notes with timing info.
 */

import { motion, AnimatePresence } from "framer-motion";
import { Check, X } from "lucide-react";
import type { TimingResult } from "@/hooks/useMetronome";

interface FeedbackOverlayProps {
  feedback: "correct" | "wrong" | null;
  expectedNote?: string;
  playedNote?: string;
  timingResult?: TimingResult | null;
  errorType?: "pitch" | "duration" | null;
  expectedDuration?: number; // in milliseconds
  actualDuration?: number; // in milliseconds
  expectedBeats?: number; // in beats
}

const TIMING_COLORS: Record<string, string> = {
  perfect: "oklch(0.70 0.18 150)",
  good: "oklch(0.82 0.17 75)",
  acceptable: "oklch(0.82 0.14 200)",
  early: "oklch(0.75 0.18 30)",
  late: "oklch(0.75 0.18 30)",
  miss: "oklch(0.65 0.24 25)",
};

export default function FeedbackOverlay({ 
  feedback, 
  expectedNote, 
  playedNote, 
  timingResult,
  errorType,
  expectedDuration,
  actualDuration,
  expectedBeats,
}: FeedbackOverlayProps) {
  
  // Calculate duration difference details
  const durationDiff = actualDuration && expectedDuration ? actualDuration - expectedDuration : null;
  const durationPercent = durationDiff && expectedDuration ? Math.round((Math.abs(durationDiff) / expectedDuration) * 100) : null;
  
  return (
    <AnimatePresence>
      {feedback && (
        <motion.div
          key={feedback + Date.now()}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.15 }}
          className="absolute inset-0 pointer-events-none flex items-center justify-center z-20"
        >
          {/* Background flash */}
          <motion.div
            initial={{ opacity: 0.15 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0"
            style={{
              background:
                feedback === "correct"
                  ? "radial-gradient(circle at center, oklch(0.82 0.17 75 / 15%) 0%, transparent 70%)"
                  : "radial-gradient(circle at center, oklch(0.65 0.24 25 / 15%) 0%, transparent 70%)",
            }}
          />

          {/* Icon + timing */}
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 1.5, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col items-center gap-1"
          >
            {feedback === "correct" ? (
              <div className="flex flex-col items-center gap-1">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{
                    background: "oklch(0.82 0.17 75 / 20%)",
                    border: "2px solid oklch(0.82 0.17 75)",
                  }}
                >
                  <Check size={20} style={{ color: "oklch(0.82 0.17 75)" }} />
                </div>
                {timingResult && (
                  <div
                    className="text-[10px] font-mono font-bold"
                    style={{ color: TIMING_COLORS[timingResult.rating] || "oklch(0.65 0.01 270)" }}
                  >
                    {timingResult.rating.toUpperCase()}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{
                    background: "oklch(0.65 0.24 25 / 20%)",
                    border: "2px solid oklch(0.65 0.24 25)",
                  }}
                >
                  <X size={20} style={{ color: "oklch(0.65 0.24 25)" }} />
                </div>
                
                {/* Error description */}
                {errorType === "pitch" && expectedNote && playedNote && (
                  <div className="text-xs font-mono font-bold text-center" style={{ color: "oklch(0.65 0.24 25)" }}>
                    Wrong Pitch
                  </div>
                )}
                {errorType === "duration" && durationDiff !== null && (
                  <div className="text-xs font-mono font-bold text-center" style={{ color: "oklch(0.75 0.18 30)" }}>
                    {durationDiff < 0 ? "Released Too Early" : "Held Too Long"}
                  </div>
                )}
                
                {/* Details */}
                <div className="text-[10px] font-mono text-center space-y-0.5" style={{ color: "oklch(0.60 0.01 270)" }}>
                  {errorType === "pitch" && expectedNote && playedNote && (
                    <div>Expected <span style={{ color: "oklch(0.70 0.18 150)" }}>{expectedNote}</span>, played <span style={{ color: "oklch(0.65 0.24 25)" }}>{playedNote}</span></div>
                  )}
                  {errorType === "duration" && expectedDuration && actualDuration && expectedBeats && (
                    <>
                      <div>Expected: <span style={{ color: "oklch(0.70 0.18 150)" }}>{expectedBeats} {expectedBeats === 1 ? 'beat' : 'beats'}</span> ({(expectedDuration / 1000).toFixed(2)}s)</div>
                      <div>You held: <span style={{ color: "oklch(0.65 0.24 25)" }}>{(actualDuration / 1000).toFixed(2)}s</span></div>
                      {durationPercent !== null && (
                        <div style={{ color: "oklch(0.75 0.18 30)" }}>
                          {durationPercent}% {durationDiff! < 0 ? 'too short' : 'too long'}
                        </div>
                      )}
                    </>
                  )}
                </div>
                
                {timingResult && timingResult.rating && (
                  <div
                    className="text-[9px] font-mono mt-0.5"
                    style={{ color: TIMING_COLORS[timingResult.rating] || "oklch(0.65 0.01 270)" }}
                  >
                    {timingResult.rating.toUpperCase()}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
